/* tslint:disable */
/* eslint-disable */
import { HttpClient, HttpContext } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { BaseService } from '../base-service';
import { ApiConfiguration } from '../api-configuration';
import { StrictHttpResponse } from '../strict-http-response';

import { ApiResponseListPendingMessageResponse } from '../models/api-response-list-pending-message-response';
import { ApiResponsePagedResultPendingMessageResponse } from '../models/api-response-paged-result-pending-message-response';
import { ApiResponsePendingMessageFilterOptions } from '../models/api-response-pending-message-filter-options';
import { ApiResponsePendingMessageImportResult } from '../models/api-response-pending-message-import-result';
import { ApiResponsePendingSendSummary } from '../models/api-response-pending-send-summary';
import { ApiResponseString } from '../models/api-response-string';
import { exportFailedMessagesCsv } from '../fn/pending-messages/export-failed-messages-csv';
import { ExportFailedMessagesCsv$Params } from '../fn/pending-messages/export-failed-messages-csv';
import { exportInvalidContactsCsv } from '../fn/pending-messages/export-invalid-contacts-csv';
import { ExportInvalidContactsCsv$Params } from '../fn/pending-messages/export-invalid-contacts-csv';
import { exportValidContactsCsv } from '../fn/pending-messages/export-valid-contacts-csv';
import { ExportValidContactsCsv$Params } from '../fn/pending-messages/export-valid-contacts-csv';
import { getMediaByMediaId } from '../fn/pending-messages/get-media-by-media-id';
import { GetMediaByMediaId$Params } from '../fn/pending-messages/get-media-by-media-id';
import { getPendingMessage } from '../fn/pending-messages/get-pending-message';
import { GetPendingMessage$Params } from '../fn/pending-messages/get-pending-message';
import { getPendingMessageFilterOptions } from '../fn/pending-messages/get-pending-message-filter-options';
import { GetPendingMessageFilterOptions$Params } from '../fn/pending-messages/get-pending-message-filter-options';
import { getSendStatus } from '../fn/pending-messages/get-send-status';
import { GetSendStatus$Params } from '../fn/pending-messages/get-send-status';
import { importPendingMessages } from '../fn/pending-messages/import-pending-messages';
import { ImportPendingMessages$Params } from '../fn/pending-messages/import-pending-messages';
import { PendingMessageResponse } from '../models/pending-message-response';
import { requestExcelExport } from '../fn/pending-messages/request-excel-export';
import { RequestExcelExport$Params } from '../fn/pending-messages/request-excel-export';
import { resendBulkPendingMessages } from '../fn/pending-messages/resend-bulk-pending-messages';
import { ResendBulkPendingMessages$Params } from '../fn/pending-messages/resend-bulk-pending-messages';
import { searchPendingMessages } from '../fn/pending-messages/search-pending-messages';
import { SearchPendingMessages$Params } from '../fn/pending-messages/search-pending-messages';
import { sendBulkPendingMessages } from '../fn/pending-messages/send-bulk-pending-messages';
import { SendBulkPendingMessages$Params } from '../fn/pending-messages/send-bulk-pending-messages';
import { updatePendingMessage } from '../fn/pending-messages/update-pending-message';
import { UpdatePendingMessage$Params } from '../fn/pending-messages/update-pending-message';


/**
 * Endpoints for managing pending messages requiring validation
 */
@Injectable({ providedIn: 'root' })
export class PendingMessagesService extends BaseService {
  constructor(config: ApiConfiguration, http: HttpClient) {
    super(config, http);
  }

  /** Path part for operation `getPendingMessage()` */
  static readonly GetPendingMessagePath = '/v1/api/pending-messages/{reference}';

  /**
   * Get pending message by reference.
   *
   * Returns pending detail including importError / importErrorReason / importErrorMessage (invalid contact). Use messagePendingReference from Open Notification create response.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getPendingMessage()` instead.
   *
   * This method doesn't expect any request body.
   */
  getPendingMessage$Response(params: GetPendingMessage$Params, context?: HttpContext): Observable<StrictHttpResponse<PendingMessageResponse>> {
    return getPendingMessage(this.http, this.rootUrl, params, context);
  }

  /**
   * Get pending message by reference.
   *
   * Returns pending detail including importError / importErrorReason / importErrorMessage (invalid contact). Use messagePendingReference from Open Notification create response.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getPendingMessage$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getPendingMessage(params: GetPendingMessage$Params, context?: HttpContext): Observable<PendingMessageResponse> {
    return this.getPendingMessage$Response(params, context).pipe(
      map((r: StrictHttpResponse<PendingMessageResponse>): PendingMessageResponse => r.body)
    );
  }

  /** Path part for operation `updatePendingMessage()` */
  static readonly UpdatePendingMessagePath = '/v1/api/pending-messages/{reference}';

  /**
   * Update pending message.
   *
   * Update phone, validationRegional, template/media, client fields, etc. A valid contactPhoneNumber clears importError; invalid phone sets importError and forces validationRegional=false.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `updatePendingMessage()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updatePendingMessage$Response(params: UpdatePendingMessage$Params, context?: HttpContext): Observable<StrictHttpResponse<PendingMessageResponse>> {
    return updatePendingMessage(this.http, this.rootUrl, params, context);
  }

  /**
   * Update pending message.
   *
   * Update phone, validationRegional, template/media, client fields, etc. A valid contactPhoneNumber clears importError; invalid phone sets importError and forces validationRegional=false.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `updatePendingMessage$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updatePendingMessage(params: UpdatePendingMessage$Params, context?: HttpContext): Observable<PendingMessageResponse> {
    return this.updatePendingMessage$Response(params, context).pipe(
      map((r: StrictHttpResponse<PendingMessageResponse>): PendingMessageResponse => r.body)
    );
  }

  /** Path part for operation `sendBulkPendingMessages()` */
  static readonly SendBulkPendingMessagesPath = '/v1/api/pending-messages/send-bulk';

  /**
   * Send pending messages.
   *
   * ASYNC (default): validated, unsent references of the caller's company are marked QUEUED and the request returns immediately; a background worker sends them at a controlled speed. Skipped: not validated, already sent, failed, import error, already queued/sending. Follow progress with GET /send-status and each row's sendStatus. SYNC (waba.pending-send.mode=SYNC): previous behaviour, sends in the request.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `sendBulkPendingMessages()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  sendBulkPendingMessages$Response(params: SendBulkPendingMessages$Params, context?: HttpContext): Observable<StrictHttpResponse<ApiResponseListPendingMessageResponse>> {
    return sendBulkPendingMessages(this.http, this.rootUrl, params, context);
  }

  /**
   * Send pending messages.
   *
   * ASYNC (default): validated, unsent references of the caller's company are marked QUEUED and the request returns immediately; a background worker sends them at a controlled speed. Skipped: not validated, already sent, failed, import error, already queued/sending. Follow progress with GET /send-status and each row's sendStatus. SYNC (waba.pending-send.mode=SYNC): previous behaviour, sends in the request.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `sendBulkPendingMessages$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  sendBulkPendingMessages(params: SendBulkPendingMessages$Params, context?: HttpContext): Observable<ApiResponseListPendingMessageResponse> {
    return this.sendBulkPendingMessages$Response(params, context).pipe(
      map((r: StrictHttpResponse<ApiResponseListPendingMessageResponse>): ApiResponseListPendingMessageResponse => r.body)
    );
  }

  /** Path part for operation `searchPendingMessages()` */
  static readonly SearchPendingMessagesPath = '/v1/api/pending-messages/search';

  /**
   * Search pending messages (paginated).
   *
   * Returns a page of pending messages including importError flags. Body: page (0-based), size, sort (ASC/DESC), sortField (createdAt|updatedAt), filter. filter.importError=true keeps only invalid contacts (Contact invalide), false keeps only valid ones (legacy rows with import_error NULL count as valid). filter.importErrorReason narrows to MISSING_PHONE or INVALID_PHONE.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `searchPendingMessages()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  searchPendingMessages$Response(params: SearchPendingMessages$Params, context?: HttpContext): Observable<StrictHttpResponse<ApiResponsePagedResultPendingMessageResponse>> {
    return searchPendingMessages(this.http, this.rootUrl, params, context);
  }

  /**
   * Search pending messages (paginated).
   *
   * Returns a page of pending messages including importError flags. Body: page (0-based), size, sort (ASC/DESC), sortField (createdAt|updatedAt), filter. filter.importError=true keeps only invalid contacts (Contact invalide), false keeps only valid ones (legacy rows with import_error NULL count as valid). filter.importErrorReason narrows to MISSING_PHONE or INVALID_PHONE.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `searchPendingMessages$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  searchPendingMessages(params: SearchPendingMessages$Params, context?: HttpContext): Observable<ApiResponsePagedResultPendingMessageResponse> {
    return this.searchPendingMessages$Response(params, context).pipe(
      map((r: StrictHttpResponse<ApiResponsePagedResultPendingMessageResponse>): ApiResponsePagedResultPendingMessageResponse => r.body)
    );
  }

  /** Path part for operation `resendBulkPendingMessages()` */
  static readonly ResendBulkPendingMessagesPath = '/v1/api/pending-messages/resend-bulk';

  /**
   * Resend pending messages.
   *
   * Queues validated messages that were previously sent or failed. Pending, invalid, already queued/sending, and unknown messages are skipped.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `resendBulkPendingMessages()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  resendBulkPendingMessages$Response(params: ResendBulkPendingMessages$Params, context?: HttpContext): Observable<StrictHttpResponse<ApiResponseListPendingMessageResponse>> {
    return resendBulkPendingMessages(this.http, this.rootUrl, params, context);
  }

  /**
   * Resend pending messages.
   *
   * Queues validated messages that were previously sent or failed. Pending, invalid, already queued/sending, and unknown messages are skipped.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `resendBulkPendingMessages$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  resendBulkPendingMessages(params: ResendBulkPendingMessages$Params, context?: HttpContext): Observable<ApiResponseListPendingMessageResponse> {
    return this.resendBulkPendingMessages$Response(params, context).pipe(
      map((r: StrictHttpResponse<ApiResponseListPendingMessageResponse>): ApiResponseListPendingMessageResponse => r.body)
    );
  }

  /** Path part for operation `importPendingMessages()` */
  static readonly ImportPendingMessagesPath = '/v1/api/pending-messages/import-csv';

  /**
   * Import pending messages from parsed CSV rows.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `importPendingMessages()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  importPendingMessages$Response(params: ImportPendingMessages$Params, context?: HttpContext): Observable<StrictHttpResponse<ApiResponsePendingMessageImportResult>> {
    return importPendingMessages(this.http, this.rootUrl, params, context);
  }

  /**
   * Import pending messages from parsed CSV rows.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `importPendingMessages$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  importPendingMessages(params: ImportPendingMessages$Params, context?: HttpContext): Observable<ApiResponsePendingMessageImportResult> {
    return this.importPendingMessages$Response(params, context).pipe(
      map((r: StrictHttpResponse<ApiResponsePendingMessageImportResult>): ApiResponsePendingMessageImportResult => r.body)
    );
  }

  /** Path part for operation `requestExcelExport()` */
  static readonly RequestExcelExportPath = '/v1/api/pending-messages/export-excel-background/{exportType}/{lang}';

  /**
   * Prepare a filtered pending-message Excel export in the background.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `requestExcelExport()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  requestExcelExport$Response(params: RequestExcelExport$Params, context?: HttpContext): Observable<StrictHttpResponse<ApiResponseString>> {
    return requestExcelExport(this.http, this.rootUrl, params, context);
  }

  /**
   * Prepare a filtered pending-message Excel export in the background.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `requestExcelExport$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  requestExcelExport(params: RequestExcelExport$Params, context?: HttpContext): Observable<ApiResponseString> {
    return this.requestExcelExport$Response(params, context).pipe(
      map((r: StrictHttpResponse<ApiResponseString>): ApiResponseString => r.body)
    );
  }

  /** Path part for operation `exportValidContactsCsv()` */
  static readonly ExportValidContactsCsvPath = '/v1/api/pending-messages/valid-contacts/export-csv';

  /**
   * Export valid pending-message contacts as CSV.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `exportValidContactsCsv()` instead.
   *
   * This method doesn't expect any request body.
   */
  exportValidContactsCsv$Response(params: ExportValidContactsCsv$Params, context?: HttpContext): Observable<StrictHttpResponse<Blob>> {
    return exportValidContactsCsv(this.http, this.rootUrl, params, context);
  }

  /**
   * Export valid pending-message contacts as CSV.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `exportValidContactsCsv$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  exportValidContactsCsv(params: ExportValidContactsCsv$Params, context?: HttpContext): Observable<Blob> {
    return this.exportValidContactsCsv$Response(params, context).pipe(
      map((r: StrictHttpResponse<Blob>): Blob => r.body)
    );
  }

  /** Path part for operation `getSendStatus()` */
  static readonly GetSendStatusPath = '/v1/api/pending-messages/send-status';

  /**
   * Send progress.
   *
   * Counts of pending messages per send status (QUEUED, SENDING, SENT, FAILED, UNKNOWN) for the caller's company. since (sendRequestedAt of the send, ISO local date-time): only rows queued at or after this time, and failed also counts messages rejected later by WhatsApp (same rule as the table). Without since: all-time counts. startedAt = sendRequestedAt of the oldest message still queued/sending. inProgress=true while messages are queued or sending.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getSendStatus()` instead.
   *
   * This method doesn't expect any request body.
   */
  getSendStatus$Response(params: GetSendStatus$Params, context?: HttpContext): Observable<StrictHttpResponse<ApiResponsePendingSendSummary>> {
    return getSendStatus(this.http, this.rootUrl, params, context);
  }

  /**
   * Send progress.
   *
   * Counts of pending messages per send status (QUEUED, SENDING, SENT, FAILED, UNKNOWN) for the caller's company. since (sendRequestedAt of the send, ISO local date-time): only rows queued at or after this time, and failed also counts messages rejected later by WhatsApp (same rule as the table). Without since: all-time counts. startedAt = sendRequestedAt of the oldest message still queued/sending. inProgress=true while messages are queued or sending.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getSendStatus$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getSendStatus(params: GetSendStatus$Params, context?: HttpContext): Observable<ApiResponsePendingSendSummary> {
    return this.getSendStatus$Response(params, context).pipe(
      map((r: StrictHttpResponse<ApiResponsePendingSendSummary>): ApiResponsePendingSendSummary => r.body)
    );
  }

  /** Path part for operation `getMediaByMediaId()` */
  static readonly GetMediaByMediaIdPath = '/v1/api/pending-messages/media/{mediaId}';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getMediaByMediaId()` instead.
   *
   * This method doesn't expect any request body.
   */
  getMediaByMediaId$Response(params: GetMediaByMediaId$Params, context?: HttpContext): Observable<StrictHttpResponse<Blob>> {
    return getMediaByMediaId(this.http, this.rootUrl, params, context);
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getMediaByMediaId$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getMediaByMediaId(params: GetMediaByMediaId$Params, context?: HttpContext): Observable<Blob> {
    return this.getMediaByMediaId$Response(params, context).pipe(
      map((r: StrictHttpResponse<Blob>): Blob => r.body)
    );
  }

  /** Path part for operation `exportInvalidContactsCsv()` */
  static readonly ExportInvalidContactsCsvPath = '/v1/api/pending-messages/invalid-contacts/export-csv';

  /**
   * Export invalid pending-message contacts as CSV.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `exportInvalidContactsCsv()` instead.
   *
   * This method doesn't expect any request body.
   */
  exportInvalidContactsCsv$Response(params: ExportInvalidContactsCsv$Params, context?: HttpContext): Observable<StrictHttpResponse<Blob>> {
    return exportInvalidContactsCsv(this.http, this.rootUrl, params, context);
  }

  /**
   * Export invalid pending-message contacts as CSV.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `exportInvalidContactsCsv$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  exportInvalidContactsCsv(params: ExportInvalidContactsCsv$Params, context?: HttpContext): Observable<Blob> {
    return this.exportInvalidContactsCsv$Response(params, context).pipe(
      map((r: StrictHttpResponse<Blob>): Blob => r.body)
    );
  }

  /** Path part for operation `getPendingMessageFilterOptions()` */
  static readonly GetPendingMessageFilterOptionsPath = '/v1/api/pending-messages/filter-options';

  /**
   * Distinct filter values for agence, secteur and tourne.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getPendingMessageFilterOptions()` instead.
   *
   * This method doesn't expect any request body.
   */
  getPendingMessageFilterOptions$Response(params: GetPendingMessageFilterOptions$Params, context?: HttpContext): Observable<StrictHttpResponse<ApiResponsePendingMessageFilterOptions>> {
    return getPendingMessageFilterOptions(this.http, this.rootUrl, params, context);
  }

  /**
   * Distinct filter values for agence, secteur and tourne.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getPendingMessageFilterOptions$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getPendingMessageFilterOptions(params: GetPendingMessageFilterOptions$Params, context?: HttpContext): Observable<ApiResponsePendingMessageFilterOptions> {
    return this.getPendingMessageFilterOptions$Response(params, context).pipe(
      map((r: StrictHttpResponse<ApiResponsePendingMessageFilterOptions>): ApiResponsePendingMessageFilterOptions => r.body)
    );
  }

  /** Path part for operation `exportFailedMessagesCsv()` */
  static readonly ExportFailedMessagesCsvPath = '/v1/api/pending-messages/failed/export-csv';

  /**
   * Export failed pending messages with their error code and reason as CSV.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `exportFailedMessagesCsv()` instead.
   *
   * This method doesn't expect any request body.
   */
  exportFailedMessagesCsv$Response(params: ExportFailedMessagesCsv$Params, context?: HttpContext): Observable<StrictHttpResponse<Blob>> {
    return exportFailedMessagesCsv(this.http, this.rootUrl, params, context);
  }

  /**
   * Export failed pending messages with their error code and reason as CSV.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `exportFailedMessagesCsv$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  exportFailedMessagesCsv(params: ExportFailedMessagesCsv$Params, context?: HttpContext): Observable<Blob> {
    return this.exportFailedMessagesCsv$Response(params, context).pipe(
      map((r: StrictHttpResponse<Blob>): Blob => r.body)
    );
  }

}
